import { Portal, selectIsBlockedByLoader, useConstant, usePortal, useWLOrderFormSelector } from '@talos/kyoko';
import { motion, type DragElastic } from 'framer-motion';
import type { PropsWithChildren } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { useNavigationContext } from '../Providers/NavigationContext';
import { getHeaderRightPortalId, StackScreenContext, useStackScreenContext } from '../hooks/StackScreenContext';
import { useStackableRouteId } from '../hooks/useStackableRouteId';
import { FooterDisclaimer } from './FooterDisclaimer';

export const StackScreenProvider = ({ children, initialTitle }: PropsWithChildren<{ initialTitle: string }>) => {
  const [title, setTitle] = useState<string>(initialTitle);
  const id = useConstant(uuid());
  return <StackScreenContext.Provider value={{ id, title, setTitle }}>{children}</StackScreenContext.Provider>;
};

export const StackScreen = ({
  children,
  initialTitle,
  canGoBack = true,
}: PropsWithChildren<{ initialTitle: string; canGoBack?: boolean }>) => {
  return (
    <StackScreenProvider initialTitle={initialTitle}>
      <StackScreenInner canGoBack={canGoBack} children={children} />
    </StackScreenProvider>
  );
};

const StackScreenInner = ({ children, canGoBack = true }: PropsWithChildren<{ canGoBack?: boolean }>) => {
  const { title, id } = useStackScreenContext();
  const navigate = useNavigate();
  // The animation would get stopped by a drag event if clicked on while animating.
  const [pointerEvents, setPointerEvents] = useState<'none' | undefined>('none');
  const { stack, setStack } = useNavigationContext();
  const dragElastic: DragElastic = useMemo(() => {
    if (canGoBack) {
      return { left: 0, right: 0.3 };
    }
    return { left: 0, right: 0 };
  }, [canGoBack]);

  useEffect(() => {
    setStack(prev => {
      return [...prev, id];
    });
    return () => {
      setStack(prev => {
        return prev.filter(item => item !== id);
      });
    };
  }, [id, setStack]);

  // determines that only the last screen in the stack is draggable
  const isActiveStack = stack.at(-1) === id;

  const isBlockedByLoader = useWLOrderFormSelector(selectIsBlockedByLoader);
  const canDrag = isActiveStack && canGoBack && !isBlockedByLoader;

  const portalId = useStackableRouteId();
  const { setPortalRef: headerRightPortalRef } = usePortal(getHeaderRightPortalId(id));

  return (
    <Portal portalId={portalId}>
      <motion.div
        key={id}
        id={id}
        // overshoot a bit so box shadow slides out
        initial={{ x: '110%' }}
        exit={{ x: '110%' }}
        animate={{ x: 0 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
        drag={canDrag ? 'x' : false}
        onAnimationStart={() => {
          setPointerEvents('none');
        }}
        onAnimationComplete={() => {
          setPointerEvents(undefined);
        }}
        dragDirectionLock={true}
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={dragElastic}
        onDragEnd={(event, info) => {
          if (canGoBack && info.offset.x > 100) {
            navigate(-1);
          }
        }}
        dragMomentum={false}
        style={{ pointerEvents, boxShadow: '-20px 30px 30px 0px rgba(0,0,0,0.6)' }}
        // consider making this absolute and render the "GCrypto+" header better
        className="fixed inset-0 z-10 flex h-full flex-col overflow-hidden bg-[#F6F9FD]"
      >
        <div className="absolute inset-0 grid grid-rows-[auto_1fr_auto] overflow-auto select-text">
          <header className="bg-color-bg-primary grid h-[40px] grid-cols-[1fr_auto_1fr] place-items-center p-2 px-4">
            <div className="flex place-self-start self-center">
              {canGoBack && (
                <button
                  data-testid="back-button"
                  onClick={() => {
                    navigate(-1);
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="14" viewBox="0 0 18 14" fill="none">
                    <path
                      d="M8.13721 13.6371C7.80778 13.9665 7.28562 13.9867 6.93213 13.6986L6.86377 13.6371L0.86377 7.63711C0.69508 7.46842 0.600201 7.23894 0.600098 7.00039C0.600098 6.7617 0.694989 6.53245 0.86377 6.36367L6.86377 0.36367C7.21524 0.0122042 7.78574 0.0122129 8.13721 0.36367C8.48829 0.715175 8.48855 1.28577 8.13721 1.63711L3.67432 6.1L16.5005 6.1C16.9974 6.10021 17.4009 6.50346 17.4009 7.00039C17.4007 7.49714 16.9972 7.90057 16.5005 7.90078L3.67432 7.90078L8.13721 12.3637L8.19873 12.432C8.48683 12.7855 8.46663 13.3077 8.13721 13.6371Z"
                      fill="white"
                    />
                  </svg>
                </button>
              )}
            </div>
            <h1 className="text-color-text-inverse self-center text-base font-semibold tracking-wide">{title}</h1>
            <div className="flex place-self-end self-center" ref={headerRightPortalRef} />
          </header>
          {children}
          <FooterDisclaimer />
        </div>
      </motion.div>
    </Portal>
  );
};
